import { call, select, takeEvery } from 'redux-saga/effects';
import {
  COLLAPSE_ACTOR,
  EDITABLE_NODE,
  MANAGE_LAYER_ACTORS,
  SAVE_LAYER_POSITION,
  STARRED_LAYER_ACTOR,
} from '@control-front-end/common/constants/graphLayers';
import { RequestStatus } from 'constants';
import api from '@control-front-end/common/sagas/api';
import {
  getGraphEls,
  getLayerById,
  makeGraphModels,
  sendReducerMsg,
  updateActorProp,
  updateActorsElProps,
  updateElProps,
  updateActorOnGraph,
} from '../../../../sagas/graph/graphHelpers';
import { saveNodePositions } from './layerHelpers';
import {
  getNewBalances,
  resubscribeBalances,
} from '../../../../sagas/graph/graphRealtime';

/**
 * Сделать редактируемым узел на слое
 */
function* editableNode({ payload }) {
  yield sendReducerMsg({
    type: EDITABLE_NODE.SUCCESS,
    payload: {
      editableElement: payload,
    },
  });
}

/**
 * Свернуть\развернуть узел на слое
 */
function* collapseActor({ payload }) {
  const { layerId, id, laId, collapsed } = payload;
  const graphL = yield select((state) => state.graphLayers);
  const findLayer = graphL.list.find((i) => i.id === layerId);
  if (findLayer.typeLayer === 'layers') {
    const { result } = yield call(api, {
      method: 'put',
      url: `/graph_layers/collapsed/${laId}/${collapsed}`,
    });
    if (result !== RequestStatus.SUCCESS) return;
  }
  const actor = findLayer.nodes.find((i) => i.id === id);
  const { nodes } = yield call(updateActorOnGraph, {
    layerId,
    actorModel: {
      ...actor.data,
      collapsed,
    },
  });
  yield sendReducerMsg({
    type: COLLAPSE_ACTOR.SUCCESS,
    payload: { nodes },
  });
}

/**
 * Отметить актор на слое
 */
function* starredActor({ payload }) {
  const { layerId, id, laId, starred } = payload;
  const { result } = yield call(api, {
    method: 'put',
    url: `/graph_layers/starred/${laId}/${starred}`,
  });
  if (result !== RequestStatus.SUCCESS) return;
  yield updateActorProp({
    type: STARRED_LAYER_ACTOR.SUCCESS,
    layerId,
    id,
    propId: 'layerStarred',
    value: starred,
  });
}

/**
 * Сохранить позиции узлов на графе
 */
function* saveLayerPosition({ payload }) {
  const { layerId, positions = [], manageLayer } = payload;
  const graphL = yield select((state) => state.graphLayers);
  const layer = graphL.list.find((i) => i.id === layerId);
  if (!layer) return;
  if (manageLayer && layer.typeLayer === 'layers') {
    const nodeIds = new Set(layer.nodes.map((i) => i.id.replace('node_', '')));
    const filteredNodes = positions.filter((i) => nodeIds.has(i.id));
    const { result } = yield call(api, {
      method: 'put',
      url: `/graph_layers/actors/${layerId}`,
      body: filteredNodes,
    });
    if (result !== RequestStatus.SUCCESS) return;
  }
  const copyNodes = yield call(saveNodePositions, { layerId, positions });
  yield sendReducerMsg({
    layerId,
    type: SAVE_LAYER_POSITION.SUCCESS,
    payload: {
      nodes: copyNodes,
      edges: yield call(getGraphEls, 'edges', layerId),
    },
  });
}

/**
 * Сформировать объекты запроса изменения слоя
 */
function createManageLayerBody(body) {
  return body.map(({ action, data }) => {
    const { type, id, position, areaPicture, model } = data;
    const obj = { action };
    if (type === 'node') {
      obj.data = {
        id: model.actorId || id,
        type,
        laId: model.laId,
        position,
        areaPicture,
      };
    } else {
      obj.data = {
        id: model.edgeId || id,
        type,
        laIdSource: data.laIdSource,
        laIdTarget: data.laIdTarget,
      };
    }
    return obj;
  });
}

/**
 * Обновить балансы узлов на слое при обновлении
 */
function* updateBalances({
  layerId,
  nodes,
  reSub = true,
  subscribeBalances = true,
}) {
  const uniqueActorsIds = new Set(nodes.map((i) => i.data.actorId));
  if (subscribeBalances)
    yield getNewBalances({ layerId, actorIds: [...uniqueActorsIds] });
  if (reSub) yield resubscribeBalances({ layerId });
}

/**
 * Добавить элементы на слой
 */
function addElements({ body, nodesMap, edgesMap }) {
  const createBody = body.filter((i) => i.action === 'create');
  const addedNodes = [];
  const addedEdges = [];
  const mapNodeIds = {};
  let nodeIndex = 0;
  let edgeIndex = 0;
  for (const { data } of createBody) {
    const { id, type, model } = data;
    const map = type === 'node' ? nodesMap : edgesMap;
    const index = type === 'node' ? nodeIndex : edgeIndex;
    const mapEl = map[index] || {};
    if (type === 'node') {
      addedNodes.push({ ...model, laId: mapEl.laId });
      mapNodeIds[id] = mapEl.laId ? `node_${mapEl.laId}` : id;
      nodeIndex += 1;
    } else if (type === 'edge') {
      addedEdges.push({ ...model, laId: mapEl.laId, edgeId: mapEl.edgeId });
      edgeIndex += 1;
    }
  }
  return { addedNodes, addedEdges, mapNodeIds };
}

/**
 * Удалить элементы со слоя
 */
function removeElements({ body, copyNodes, copyEdges }) {
  const deleteBody = body.filter((i) => i.action === 'delete');
  for (const { data } of deleteBody) {
    const { type, model, withDuplicates } = data;
    const fieldNameId = type === 'node' ? 'actorId' : 'edgeId';
    const fieldId = type === 'node' ? model.actorId : model.edgeId;
    const list = type === 'node' ? copyNodes : copyEdges;
    const props = { status: 'removed' };
    if (withDuplicates) {
      updateActorsElProps({ [fieldNameId]: fieldId, list, props });
    } else {
      updateElProps(list, model.id, props);
    }
  }
}

/**
 * Получить связи удаляемых узлов
 */
function getLinkedRemovedEdges({ body, copyEdges }) {
  const deletedEdges = [];
  for (const { action, data } of body) {
    if (action === 'create' || data.type === 'edge') continue;
    for (const e of copyEdges) {
      const { edgeId, source, target, status } = e.data;
      if (
        (source === data.model.id || target === data.model.id) &&
        status !== 'removed'
      ) {
        deletedEdges.push({
          action: 'delete',
          data: { id: edgeId, model: e.data, type: 'edge' },
        });
      }
    }
  }
  return deletedEdges;
}

/**
 * Добавить акторы в слой
 */
export function* manageLayerElements({ payload }) {
  const {
    layerId,
    body,
    activeElement,
    activeType,
    editableElement,
    withReq = true,
    subscribeBalances = true,
    reduxEvent = true,
  } = payload;
  if (!layerId) return;
  const copyNodes = yield call(getGraphEls, 'nodes', layerId);
  const copyEdges = yield call(getGraphEls, 'edges', layerId);
  const removedEdges = getLinkedRemovedEdges({ body, copyEdges });
  body.push(...removedEdges);
  let nodesMap = {};
  let edgesMap = {};
  if (withReq) {
    const { result, data: resp } = yield call(api, {
      method: 'post',
      url: `/graph_layers/actors/${layerId}`,
      body: createManageLayerBody(body),
    });
    if (result !== RequestStatus.SUCCESS) return;
    nodesMap = resp.data.nodesMap;
    edgesMap = resp.data.edgesMap;
  }
  const layerModel = yield getLayerById(layerId);
  if (!layerModel) {
    return { nodesMap, edgesMap };
  }
  const { addedNodes, addedEdges, mapNodeIds } = addElements({
    body,
    nodesMap,
    edgesMap,
  });
  removeElements({ body, copyNodes, copyEdges });
  const { nodes, edges } = yield call(makeGraphModels, {
    nodes: addedNodes,
    edges: addedEdges,
    isTree: layerModel.type === 'tree',
  });
  copyNodes.push(...nodes);
  copyEdges.push(...edges);
  if (reduxEvent) {
    yield sendReducerMsg({
      type: MANAGE_LAYER_ACTORS.SUCCESS,
      payload: {
        nodes: copyNodes,
        edges: copyEdges,
        activeElement: mapNodeIds[activeElement] || layerModel.activeElement,
        activeType,
        editableElement:
          mapNodeIds[editableElement] || layerModel.editableElement,
      },
      layerId,
    });
  }
  const isLayerCommenting =
    addedNodes.length === 1 &&
    addedNodes[0].data &&
    addedNodes[0].data.channel === 'layerArea';
  if (!isLayerCommenting) {
    yield updateBalances({
      layerId,
      nodes: copyNodes,
      reSub: !!addedNodes.length,
      subscribeBalances,
    });
  }
  return { nodesMap, edgesMap, addedNodes, addedEdges };
}

function* layerElements() {
  yield takeEvery(STARRED_LAYER_ACTOR.REQUEST, starredActor);
  yield takeEvery(EDITABLE_NODE.REQUEST, editableNode);
  yield takeEvery(COLLAPSE_ACTOR.REQUEST, collapseActor);
  yield takeEvery(SAVE_LAYER_POSITION.REQUEST, saveLayerPosition);
  yield takeEvery(MANAGE_LAYER_ACTORS.REQUEST, manageLayerElements);
}

export default layerElements;
